import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';

import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { IPSHttpErrorInterceptor, IPSStylesModule, IPSTenantKeyService, IPSxTenantKeyInterceptor, provideIPSAuth, provideObservability } from '@ips/common-angular';
import Aura from '@primeng/themes/aura';
import { OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { Configuration as CasefileConfiguration, CaseFileService } from 'com.trumbull.phx.casefile.api-client';
import { Configuration as DocumentConfiguration, DocumentService } from 'com.trumbull.phx.document.api-client';
import { Configuration as OrderConfiguration, OrderService } from 'com.trumbull.phx.order.api-client';
import { Configuration as VocabularyConfiguration, VocabularyService } from 'com.trumbull.phx.vocabulary.api-client';
import { Configuration as ClaimConfiguration, ClaimItemService } from 'com.trumbull.phx.claim.api-client';
import { provideToastr } from 'ngx-toastr';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';

import { Configuration as RecordRetrievalConfiguration, RecordRetrievalService } from 'com.trumbull.phx.recordretrievalobjective.api-client';

import packageJson from '../../../client-portal-app/package.json';
import { environment } from '@env/environment';
import { GlobalErrorHandler } from './interceptors/global-error-handler';

export const appConfig: ApplicationConfig = {
  providers: [
    IPSTenantKeyService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration(withEventReplay()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          prefix: 'p',
          darkModeSelector: 'system',
          cssLayer: false
        }
      }
    }),
    provideHttpClient(
        withFetch(),
        withInterceptors([IPSxTenantKeyInterceptor, IPSHttpErrorInterceptor])
    ),
    provideOAuthClient(),
    OAuthService,
    provideIPSAuth(),
    provideToastr(),
    OrderConfiguration,
    CasefileConfiguration,
    DocumentConfiguration,
    VocabularyConfiguration,
    RecordRetrievalConfiguration,
    ClaimConfiguration,
    {
      provide: OrderService,
      deps: [HttpClient, OrderConfiguration],
      useFactory: (httpClient: HttpClient, configuration: OrderConfiguration) => new OrderService(httpClient, '/api/order', configuration)
    },
    {
      provide: CaseFileService,
      deps: [HttpClient, CasefileConfiguration],
      useFactory: (httpClient: HttpClient, configuration: CasefileConfiguration) => new CaseFileService(httpClient, '/api/casefile', configuration)
    },
    {
      provide: DocumentService,
      deps: [HttpClient, DocumentConfiguration],
      useFactory: (httpClient: HttpClient, configuration: DocumentConfiguration) => new DocumentService(httpClient, '/api/document', configuration)
    },
    {
      provide: VocabularyService,
      deps: [HttpClient, VocabularyConfiguration],
      useFactory: (httpClient: HttpClient, configuration: VocabularyConfiguration) => new VocabularyService(httpClient, '/api/vocab', configuration)
    },
    {
      provide: RecordRetrievalService,
      deps: [HttpClient, RecordRetrievalConfiguration],
      useFactory: (httpClient: HttpClient, configuration: RecordRetrievalConfiguration) => new RecordRetrievalService(httpClient, '/api/record-retrieval-objective', configuration)
    },
    {
      provide: ClaimItemService,
      deps: [HttpClient, ClaimConfiguration],
      useFactory: (httpClient: HttpClient, configuration: ClaimConfiguration) => new ClaimItemService(httpClient, '/api/claim', configuration)
    },
    environment.observabilityEnabled ? provideObservability({
      appName: packageJson.name,
      version: packageJson.version,
    }) : [],
    IPSStylesModule,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
  ]
};
